import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { useAdmin } from "../../../context/AdminContext";
import { useNavigate } from "react-router-dom";

const MAX_NAME = 80;
const MAX_DETAILS = 1000;
const MAX_IMAGE_SIZE = 2 * 1024 * 1024;
const MAX_OTHER_IMAGES = 10;
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const CATEGORIES = [
  "Hotels & Restaurants",
  "Tea Factories & Industries",
  "Home & Living",
  "Others",
];

const isBlobUrl = (value) =>
  typeof value === "string" && value.startsWith("blob:");

const AddProject = () => {
  const { admin, token } = useAdmin();
  const navigate = useNavigate();
  const coverInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [name, setName] = useState("");
  const [details, setDetails] = useState("");
  const [category, setCategory] = useState("");
  const [starred, setStarred] = useState(false);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [otherImages, setOtherImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ global: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const revokePreview = (preview) => {
    if (isBlobUrl(preview)) {
      URL.revokeObjectURL(preview);
    }
  };

  useEffect(() => {
    return () => {
      revokePreview(coverPreview);
      otherImages.forEach((img) => revokePreview(img.preview));
    };
  }, [coverPreview, otherImages]);

  const validateImage = (file) => {
    if (!file) return "No file selected";

    const validExt = /\.(jpg|jpeg|png|webp)$/i.test(file.name);
    const validMime = !file.type || ACCEPTED_IMAGE_TYPES.includes(file.type);

    if (!validExt || !validMime)
      return "Only JPG, PNG, and WebP images are allowed";
    if (file.size > MAX_IMAGE_SIZE) return "Image must be under 2MB";

    return null;
  };

  const compressImage = async (file) => {
    try {
      return await imageCompression(file, {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      });
    } catch {
      return file;
    }
  };

  const handleCoverChange = async (e) => {
    const file = e.target.files?.[0];
    const validationError = validateImage(file);

    if (validationError) {
      setError(validationError);
      return;
    }

    const compressed = await compressImage(file);
    revokePreview(coverPreview);

    setCoverImage(compressed);
    setCoverPreview(URL.createObjectURL(compressed));
    setError("");
  };

  const removeCover = () => {
    revokePreview(coverPreview);
    setCoverImage(null);
    setCoverPreview(null);

    if (coverInputRef.current) {
      coverInputRef.current.value = "";
    }
  };

  const handleGalleryImages = async (files) => {
    const selectedFiles = files.filter((file) => file?.type || file?.name);
    const totalImages = otherImages.length + selectedFiles.length;

    if (totalImages > MAX_OTHER_IMAGES) {
      setError(`Max ${MAX_OTHER_IMAGES} gallery images allowed`);
      return;
    }

    const processedImages = [];

    for (const file of selectedFiles) {
      const validationError = validateImage(file);

      if (validationError) {
        setError(validationError);
        return;
      }

      const compressed = await compressImage(file);
      processedImages.push({
        file: compressed,
        preview: URL.createObjectURL(compressed),
      });
    }

    setOtherImages((prev) => [...prev, ...processedImages]);
    setError("");
  };

  const handleOtherImagesChange = (e) => {
    handleGalleryImages([...e.target.files]);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleGalleryImages([...e.dataTransfer.files]);
  };

  const handleDragOver = (e) => e.preventDefault();

  const removeOtherImage = (index) => {
    setOtherImages((prev) =>
      prev.filter((img, i) => {
        if (i === index) {
          revokePreview(img.preview);
          return false;
        }
        return true;
      }),
    );
  };

  const resetForm = () => {
    revokePreview(coverPreview);
    otherImages.forEach((img) => revokePreview(img.preview));

    setName("");
    setDetails("");
    setCategory("");
    setStarred(false);
    setCoverImage(null);
    setCoverPreview(null);
    setOtherImages([]);
    setUploadProgress({ global: 0 });
    setError("");

    if (coverInputRef.current) {
      coverInputRef.current.value = "";
    }

    if (galleryInputRef.current) {
      galleryInputRef.current.value = "";
    }
  };

  const validateForm = () => {
    const trimmedName = name.trim();
    const trimmedDetails = details.trim();

    if (!trimmedName) return "Project name is required";
    if (!trimmedDetails) return "Project details are required";
    if (!category || !CATEGORIES.includes(category))
      return "Select a valid category";
    if (!coverImage) return "Cover image is required";

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setLoading(true);
      setError("");
      setUploadProgress({ global: 0 });

      const authToken =
        token || admin?.token || localStorage.getItem("adminToken");
      if (!authToken) {
        throw new Error("Not authenticated");
      }

      const formData = new FormData();
      formData.append("name", name.trim());
      formData.append("details", details.trim());
      formData.append("category", category);
      formData.append("starred", String(starred));
      formData.append("coverImage", coverImage);

      otherImages.forEach((img) => formData.append("images", img.file));

  const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

      await axios.post(`${API}/api/projects`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) return;
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadProgress({ global: percent });
        },
      });

      alert("Project added successfully!");
      resetForm();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to add project",
      );
    } finally {
      setLoading(false);
    }
  };

  const remainingImages = MAX_OTHER_IMAGES - otherImages.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-white p-6">
      <button
        type="button"
        onClick={() => navigate("/admin/dashboard")}
        className="mb-6 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition"
      >
        ← Back to Dashboard
      </button>

      <form onSubmit={handleSubmit} className="max-w-5xl mx-auto space-y-6">
        <div className="bg-white/10 p-5 rounded-2xl border border-white/10">
          <h1 className="text-2xl font-semibold">Add New Project</h1>
          <p className="text-gray-400 text-sm">
            Fill project details and upload gallery images
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="bg-red-500/10 border border-red-500 text-red-300 p-3 rounded-lg"
          >
            {error}
          </div>
        )}

        <div className="bg-white/10 p-6 rounded-2xl space-y-4 border border-white/10">
          <div className="grid gap-2">
            <label
              htmlFor="project-name"
              className="text-sm font-medium text-gray-200"
            >
              Project Name
            </label>
            <input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_NAME}
              placeholder="Project Name"
              autoComplete="off"
              required
              className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-green-500"
            />
            <div className="text-xs text-gray-400 mt-1 text-right">
              {MAX_NAME - name.length} characters remaining
            </div>
          </div>

          <div className="grid gap-2">
            <label
              htmlFor="project-category"
              className="text-sm font-medium text-gray-200"
            >
              Category
            </label>
            <select
              id="project-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
              className="w-full p-3 rounded-lg bg-gray-800 border border-white/10 focus:outline-none focus:border-green-500"
            >
              <option value="">Select Category</option>
              {CATEGORIES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-2">
            <label
              htmlFor="project-details"
              className="text-sm font-medium text-gray-200"
            >
              Details
            </label>
            <textarea
              id="project-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={MAX_DETAILS}
              rows={5}
              placeholder="Project details..."
              required
              className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:outline-none focus:border-green-500"
            />
            <div className="text-xs text-gray-400 mt-1 text-right">
              {MAX_DETAILS - details.length} characters remaining
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={starred}
              onChange={(e) => setStarred(e.target.checked)}
            />
            Feature on homepage
          </label>
        </div>

        <div className="bg-white/10 p-6 rounded-2xl border border-white/10 space-y-3">
          <h2 className="font-semibold">Cover Image</h2>
          <p className="text-sm text-gray-400">
            Upload the main image used on project cards and the details page.
          </p>

          <input
            ref={coverInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            onChange={handleCoverChange}
          />

          {coverPreview && (
            <div className="relative w-40">
              <img
                src={coverPreview}
                alt="Project cover preview"
                className="w-40 h-40 object-cover rounded-xl border border-white/10"
              />
              <button
                type="button"
                onClick={removeCover}
                className="absolute top-2 right-2 bg-red-600 w-7 h-7 rounded-full"
              >
                ×
              </button>
            </div>
          )}
        </div>

        <div className="bg-white/10 p-6 rounded-2xl border border-white/10 space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="font-semibold">Project Gallery</h2>
              <p className="text-sm text-gray-400 mt-1">
                Add supporting visuals for the completed project gallery.
              </p>
            </div>
            <span className="text-xs text-gray-400 shrink-0">
              {remainingImages} slots remaining
            </span>
          </div>

          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-white/20 p-8 rounded-xl text-center text-gray-300 hover:border-green-500 transition"
          >
            Drag & drop gallery images here or upload below
            <input
              ref={galleryInputRef}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              onChange={handleOtherImagesChange}
              className="mt-3 block mx-auto"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {otherImages.map((img, index) => (
              <div key={index} className="relative">
                <img
                  src={img.preview}
                  alt={`Project gallery ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => removeOtherImage(index)}
                  className="absolute top-1 right-1 bg-black/70 w-6 h-6 rounded-full"
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          {uploadProgress.global > 0 && (
            <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all"
                style={{ width: `${uploadProgress.global}%` }}
              />
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed py-3 rounded-xl font-semibold"
        >
          {loading ? "Saving..." : "Add Project"}
        </button>
      </form>
    </div>
  );
};

export default AddProject;
